/** @format */

import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    TextField,
    InputAdornment,
    IconButton,
    Alert,
    Divider,
    CircularProgress,
    Chip,
} from "@mui/material";
import {
    CloseOutlined,
    Visibility,
    VisibilityOff,
    PersonOutline,
    LockOutlined,
} from "@mui/icons-material";
import { AppDispatch } from "../../redux/store/store";
import { setBusinessUserName } from "../../redux/slices/businessAuthSlice";
import {
    fetchMyAccountApi,
    updateMyProfileApi,
    changeMyPasswordApi,
    IMyAccount,
} from "../../redux/services/businessAuthService";
import { CollectPrimaryButton } from "./styles";
import { colors } from "../../utility/types/constant";

// ─────────────────────────────────────────────────────────────────────────
// CollectMyAccountModal — self-service account panel for the signed-in
// BusinessUser. Lets them view their identity (email + role read-only),
// edit their display name, and change their password. Fetches a fresh
// profile every time it opens so the role/label reflect the active workspace.
// ─────────────────────────────────────────────────────────────────────────

interface Props {
    open: boolean;
    onClose: () => void;
    businessId: string | null;
}

const fieldSx = {
    "& .MuiOutlinedInput-root": { borderRadius: "10px" },
    "& .MuiInputLabel-root": {
        fontFamily: "'Montserrat', sans-serif",
        fontSize: "0.85rem",
    },
    "& .MuiOutlinedInput-input": {
        fontFamily: "'Montserrat', sans-serif",
        fontSize: "0.9rem",
    },
} as const;

const CollectMyAccountModal: React.FC<Props> = ({
    open,
    onClose,
    businessId,
}) => {
    const dispatch = useDispatch<AppDispatch>();

    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [account, setAccount] = useState<IMyAccount | null>(null);

    // Profile form
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [savingProfile, setSavingProfile] = useState(false);
    const [profileError, setProfileError] = useState<string | null>(null);
    const [profileSuccess, setProfileSuccess] = useState<string | null>(null);

    // Password form
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showCurrent, setShowCurrent] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [savingPassword, setSavingPassword] = useState(false);
    const [passwordError, setPasswordError] = useState<string | null>(null);
    const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);

    // Load the profile each time the modal opens; reset all transient state.
    useEffect(() => {
        if (!open) return;
        setProfileError(null);
        setProfileSuccess(null);
        setPasswordError(null);
        setPasswordSuccess(null);
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");

        if (!businessId) {
            setLoadError("No active workspace selected.");
            return;
        }

        let cancelled = false;
        setLoading(true);
        setLoadError(null);
        fetchMyAccountApi(businessId)
            .then((data) => {
                if (cancelled) return;
                setAccount(data);
                setFirstName(data.firstName || "");
                setLastName(data.lastName || "");
            })
            .catch((e: any) => {
                if (cancelled) return;
                setLoadError(e?.message || "Failed to load your account.");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [open, businessId]);

    const trimmedFirst = firstName.trim();
    const trimmedLast = lastName.trim();
    const profileDirty =
        !!account &&
        (trimmedFirst !== (account.firstName || "") ||
            trimmedLast !== (account.lastName || ""));

    const handleSaveProfile = async () => {
        setProfileError(null);
        setProfileSuccess(null);
        if (!trimmedFirst || !trimmedLast) {
            setProfileError("First and last name are required.");
            return;
        }
        if (trimmedFirst.length > 80 || trimmedLast.length > 80) {
            setProfileError("Names must be 80 characters or fewer.");
            return;
        }
        setSavingProfile(true);
        try {
            const updated = await updateMyProfileApi({
                firstName: trimmedFirst,
                lastName: trimmedLast,
            });
            setAccount((prev) =>
                prev
                    ? {
                        ...prev,
                        firstName: updated.firstName,
                        lastName: updated.lastName,
                    }
                    : prev,
            );
            dispatch(
                setBusinessUserName({
                    firstName: updated.firstName,
                    lastName: updated.lastName,
                }),
            );
            setProfileSuccess("Profile updated.");
        } catch (e: any) {
            setProfileError(e?.message || "Failed to update your profile.");
        } finally {
            setSavingProfile(false);
        }
    };

    const handleChangePassword = async () => {
        setPasswordError(null);
        setPasswordSuccess(null);
        if (!currentPassword || !newPassword || !confirmPassword) {
            setPasswordError("All password fields are required.");
            return;
        }
        if (newPassword.length < 8) {
            setPasswordError("New password must be at least 8 characters.");
            return;
        }
        if (newPassword !== confirmPassword) {
            setPasswordError("New password and confirmation do not match.");
            return;
        }
        if (newPassword === currentPassword) {
            setPasswordError(
                "New password must be different from your current password.",
            );
            return;
        }
        setSavingPassword(true);
        try {
            await changeMyPasswordApi({ currentPassword, newPassword });
            setPasswordSuccess("Password updated.");
            setCurrentPassword("");
            setNewPassword("");
            setConfirmPassword("");
        } catch (e: any) {
            setPasswordError(e?.message || "Failed to update your password.");
        } finally {
            setSavingPassword(false);
        }
    };

    const labelSx = {
        fontFamily: "'Montserrat', sans-serif",
        fontWeight: 700,
        fontSize: "0.7rem",
        letterSpacing: "0.08em",
        textTransform: "uppercase" as const,
        color: "#9ca3af",
    };

    const valueSx = {
        fontFamily: "'Montserrat', sans-serif",
        fontWeight: 600,
        fontSize: "0.92rem",
        color: colors.primary,
        wordBreak: "break-word" as const,
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: "16px" } }}
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    pb: 1,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                    <PersonOutline sx={{ color: colors.mainSecondary }} />
                    <Typography
                        sx={{
                            fontFamily: "'Montserrat', sans-serif",
                            fontWeight: 800,
                            fontSize: "1.1rem",
                            color: colors.primary,
                        }}
                    >
                        My Account
                    </Typography>
                </Box>
                <IconButton onClick={onClose} size="small">
                    <CloseOutlined fontSize="small" />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ pt: 2 }}>
                {loading ? (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            py: 4,
                        }}
                    >
                        <CircularProgress size={28} />
                    </Box>
                ) : loadError ? (
                    <Alert severity="error">{loadError}</Alert>
                ) : account ? (
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 2.5,
                        }}
                    >
                        {/* Identity (read-only) */}
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: 2,
                            }}
                        >
                            <Box>
                                <Typography sx={labelSx}>Email</Typography>
                                <Typography sx={valueSx}>
                                    {account.email}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography sx={labelSx}>Role</Typography>
                                <Chip
                                    label={account.roleLabel}
                                    size="small"
                                    sx={{
                                        mt: 0.25,
                                        fontFamily:
                                            "'Montserrat', sans-serif",
                                        fontWeight: 700,
                                        fontSize: "0.72rem",
                                        backgroundColor:
                                            "rgba(30, 193, 149, 0.12)",
                                        color: colors.primary,
                                    }}
                                />
                            </Box>
                        </Box>

                        <Divider />

                        {/* Profile */}
                        <Box>
                            <Typography
                                sx={{
                                    fontFamily: "'Montserrat', sans-serif",
                                    fontWeight: 800,
                                    fontSize: "0.82rem",
                                    color: colors.primary,
                                    mb: 1.5,
                                }}
                            >
                                Profile
                            </Typography>
                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr",
                                    gap: 2,
                                }}
                            >
                                <TextField
                                    label="First name"
                                    value={firstName}
                                    onChange={(e) =>
                                        setFirstName(e.target.value)
                                    }
                                    size="small"
                                    fullWidth
                                    inputProps={{ maxLength: 80 }}
                                    sx={fieldSx}
                                />
                                <TextField
                                    label="Last name"
                                    value={lastName}
                                    onChange={(e) =>
                                        setLastName(e.target.value)
                                    }
                                    size="small"
                                    fullWidth
                                    inputProps={{ maxLength: 80 }}
                                    sx={fieldSx}
                                />
                            </Box>
                            {profileError && (
                                <Alert severity="error" sx={{ mt: 1.5 }}>
                                    {profileError}
                                </Alert>
                            )}
                            {profileSuccess && (
                                <Alert severity="success" sx={{ mt: 1.5 }}>
                                    {profileSuccess}
                                </Alert>
                            )}
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "flex-end",
                                    mt: 1.5,
                                }}
                            >
                                <CollectPrimaryButton
                                    onClick={handleSaveProfile}
                                    disabled={
                                        savingProfile || !profileDirty
                                    }
                                    startIcon={
                                        savingProfile ? (
                                            <CircularProgress
                                                size={16}
                                                sx={{ color: "#fff" }}
                                            />
                                        ) : undefined
                                    }
                                >
                                    {savingProfile
                                        ? "Saving..."
                                        : "Save changes"}
                                </CollectPrimaryButton>
                            </Box>
                        </Box>

                        <Divider />

                        {/* Password */}
                        <Box>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.75,
                                    mb: 1.5,
                                }}
                            >
                                <LockOutlined
                                    sx={{
                                        fontSize: "1rem",
                                        color: colors.primary,
                                    }}
                                />
                                <Typography
                                    sx={{
                                        fontFamily:
                                            "'Montserrat', sans-serif",
                                        fontWeight: 800,
                                        fontSize: "0.82rem",
                                        color: colors.primary,
                                    }}
                                >
                                    Change password
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 2,
                                }}
                            >
                                <TextField
                                    label="Current password"
                                    type={
                                        showCurrent ? "text" : "password"
                                    }
                                    value={currentPassword}
                                    onChange={(e) =>
                                        setCurrentPassword(e.target.value)
                                    }
                                    size="small"
                                    fullWidth
                                    autoComplete="current-password"
                                    sx={fieldSx}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                        setShowCurrent(
                                                            (s) => !s,
                                                        )
                                                    }
                                                    edge="end"
                                                >
                                                    {showCurrent ? (
                                                        <VisibilityOff fontSize="small" />
                                                    ) : (
                                                        <Visibility fontSize="small" />
                                                    )}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <TextField
                                    label="New password"
                                    type={showNew ? "text" : "password"}
                                    value={newPassword}
                                    onChange={(e) =>
                                        setNewPassword(e.target.value)
                                    }
                                    size="small"
                                    fullWidth
                                    autoComplete="new-password"
                                    helperText="At least 8 characters."
                                    sx={fieldSx}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                        setShowNew(
                                                            (s) => !s,
                                                        )
                                                    }
                                                    edge="end"
                                                >
                                                    {showNew ? (
                                                        <VisibilityOff fontSize="small" />
                                                    ) : (
                                                        <Visibility fontSize="small" />
                                                    )}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <TextField
                                    label="Confirm new password"
                                    type={
                                        showConfirm ? "text" : "password"
                                    }
                                    value={confirmPassword}
                                    onChange={(e) =>
                                        setConfirmPassword(e.target.value)
                                    }
                                    size="small"
                                    fullWidth
                                    autoComplete="new-password"
                                    sx={fieldSx}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                        setShowConfirm(
                                                            (s) => !s,
                                                        )
                                                    }
                                                    edge="end"
                                                >
                                                    {showConfirm ? (
                                                        <VisibilityOff fontSize="small" />
                                                    ) : (
                                                        <Visibility fontSize="small" />
                                                    )}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Box>
                            {passwordError && (
                                <Alert severity="error" sx={{ mt: 1.5 }}>
                                    {passwordError}
                                </Alert>
                            )}
                            {passwordSuccess && (
                                <Alert severity="success" sx={{ mt: 1.5 }}>
                                    {passwordSuccess}
                                </Alert>
                            )}
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "flex-end",
                                    mt: 1.5,
                                }}
                            >
                                <CollectPrimaryButton
                                    onClick={handleChangePassword}
                                    disabled={
                                        savingPassword ||
                                        !currentPassword ||
                                        !newPassword ||
                                        !confirmPassword
                                    }
                                    startIcon={
                                        savingPassword ? (
                                            <CircularProgress
                                                size={16}
                                                sx={{ color: "#fff" }}
                                            />
                                        ) : undefined
                                    }
                                >
                                    {savingPassword
                                        ? "Updating..."
                                        : "Update password"}
                                </CollectPrimaryButton>
                            </Box>
                        </Box>
                    </Box>
                ) : null}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Typography
                    onClick={onClose}
                    sx={{
                        cursor: "pointer",
                        fontFamily: "'Montserrat', sans-serif",
                        fontWeight: 600,
                        fontSize: "0.85rem",
                        color: "#6b7280",
                        "&:hover": { color: colors.primary },
                    }}
                >
                    Close
                </Typography>
            </DialogActions>
        </Dialog>
    );
};

export default CollectMyAccountModal;
